import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import type { FormikHelpers } from 'formik';
import { Formik } from 'formik';
import { cleanCpfAndCnpj } from 'v2/helpers/cpfCnpj';
import { CurrencyId } from 'v2/interfaces/financial';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';

import { BankData } from './BankData';
import { BankReceipt } from './BankReceipt';
import { StepperProvider } from './context';
import { Documents } from './Documents';
import { Final } from './Final';
import type { NewBankAccount } from './form';
import { accountBankType, getSchema, initialValues } from './form';
import { Resume } from './Resume';
import { Verify } from './Verify';
import { Wizard } from './Wizard';

type StepperProps = {
  initialStep?: number;
  handleSubmit: (values: NewBankAccount, formikHelpers: FormikHelpers<NewBankAccount>) => void;
};

type ReactRouterDomState = {
  bankAccountId: number;
  description: string;
  step: number;
};

function Stepper({ initialStep, handleSubmit }: StepperProps) {
  const location = useLocation<ReactRouterDomState>();

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const isCrossborder = user?.international_account || user?.crossborder;
  const isEdit = !!location?.state;

  const [getBankAccount, bankAccount] = useCallbackObservable(
    () => financialService.getBankAccount(location?.state?.bankAccountId),
    []
  );

  React.useEffect(() => {
    if (!!location?.state?.bankAccountId) getBankAccount();
  }, [getBankAccount, location]);

  const stepsNodes = React.useMemo(
    () => [
      <BankData key='bank-data' />,
      <Documents key='documents' />,
      <BankReceipt key='bank-receipt' />,
      <Resume key='resume' />,
      <Verify key='verify' />,
      <Final key='final' />
    ],
    []
  );

  if (!user || (isEdit && !bankAccount)) return null;

  if (bankAccount) {
    const files: Record<string, any> = {};

    if (bankAccount?.document_image_front) files['document_image_front'] = bankAccount.document_image_front;
    if (bankAccount?.document_image_back) files['document_image_back'] = bankAccount.document_image_back;
    if (bankAccount?.address_image) files['address_image'] = bankAccount.address_image;
    if (bankAccount?.personal_document_video) files['personal_document_video'] = bankAccount.personal_document_video;

    sessionStorage.setItem('inputFiles', JSON.stringify(files));
  }

  const operationIdSavedToInput = (operationId: string) => {
    if (!operationId) {
      return '';
    }

    return operationId.padStart(3, '0');
  };

  const bankAccountDataToEdit = {
    ...bankAccount,
    currency: bankAccount?.currency?.id,
    document_number: cleanCpfAndCnpj(bankAccount?.document_number),
    type: bankAccount?.type === 'Conta Poupança' ? accountBankType.CONTA_POUPANCA : accountBankType.CONTA_CORRENTE,
    account_nature: bankAccount?.account_nature === 'Conta pessoa Jurídica' ? 'J' : 'F',
    operation_id: operationIdSavedToInput(bankAccount?.operation_id),
    bank_receipt_image: initialValues.bank_receipt_image,
    document_image_type: bankAccount?.document_image_type,
    document_image_front: initialValues.document_image_front,
    document_image_back: initialValues.document_image_back,
    selfie_video: initialValues.selfie_video
  };

  const formikValues = {
    ...(bankAccount ? bankAccountDataToEdit : initialValues),
    currency: isCrossborder ? CurrencyId.USD : CurrencyId.BRL,
    name: user.full_name
  };

  const errorObj = {
    step: location?.state?.step,
    description: location?.state?.description
  };

  return (
    <div className='relative mx-auto mt-8 w-full max-w-129.5 font-sans'>
      <Formik
        onSubmit={handleSubmit}
        initialValues={formikValues}
        validationSchema={getSchema(isCrossborder)}
        validateOnMount
      >
        <StepperProvider steps={stepsNodes} initialStep={initialStep} error={isEdit ? errorObj : null}>
          <Wizard />
        </StepperProvider>
      </Formik>
    </div>
  );
}

export { Stepper };
